import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import TextField from 'ui-library/inputs/TextField';
import Modal from 'ui-library/modals/Modal';
import {getUserMessage} from 'util/Net';
import {withLoadingScreen} from 'util/Promises';

import {GatewayFieldsForm, useGatewayFields} from './GatewayFieldsForm';
import {GatewayType, S3_FIELD_IDS} from './InstrumentGatewayConstants';
import {createGateway} from './InstrumentGatewayDAO';

export function getBucketPolicy(bucketName: string, benchlingRoleArn: string): string {
  return JSON.stringify(
    {
      Sid: 'BenchlingConnect',
      Effect: 'Allow',
      Principal: {
        AWS: benchlingRoleArn,
      },
      Action: ['s3:ListBucket', 's3:GetObject', 's3:PutObject'],
      Resource: [`arn:aws:s3:::${bucketName}`, `arn:aws:s3:::${bucketName}/*`],
      Condition: {
        StringEquals: {
          'aws:PrincipalTag/TenantId': BenchlingConfig.TENANT_ID,
        },
      },
    },
    null,
    2
  );
}

function openBucketPolicyTab(bucketName: string, benchlingRoleArn: string): void {
  const blob = new Blob([getBucketPolicy(bucketName, benchlingRoleArn)], {type: 'application/json'});
  const url = window.URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener noreferrer');
}

interface ViewBucketInfoModalProps {
  roleArn: string;
  onRequestClose: () => void;
  gatewayName: string;
  bucketName: string;
}

export function ViewBucketInfoModal(props: ViewBucketInfoModalProps): JSX.Element {
  const {bucketName, roleArn, gatewayName, onRequestClose} = props;
  return (
    <Modal
      buttons={[
        {
          style: ButtonStyle.PRIMARY,
          text: 'Generate policy',
          disabled: !bucketName,
          onClick: () => {
            openBucketPolicyTab(bucketName, roleArn);
          },
          submit: false,
        },
        {
          style: ButtonStyle.PRIMARY,
          text: 'Close',
          cancel: true,
        },
      ]}
      title="S3 Bucket information"
      onRequestClose={onRequestClose}
      closeable={true}
    >
      The following cloud gateway is connected to an AWS S3 bucket.
      <br />
      <br />
      <TextField label="Gateway name" value={gatewayName} disabled={true} onChange={() => {}} />
      <br />
      <TextField label="Bucket" value={bucketName} disabled={true} onChange={() => {}} />
      <br />
      <TextField label="Benchling role ARN" value={roleArn} disabled={true} onChange={() => {}} />
    </Modal>
  );
}

interface CreateS3GatewayModalProps {
  roleArn: string;
  onRequestClose: () => void;
  onSave: () => void;
}

const GENERIC_VALIDATION_FAILURE_MESSAGE = 'Validation failed. Please check your input and try again.';

export function CreateS3GatewayModal(props: CreateS3GatewayModalProps): JSX.Element {
  const {roleArn, onRequestClose, onSave} = props;
  const [error, setError] = useState<{fieldId: string | null; message: string} | null>(null);

  const [gatewayName, setGatewayName] = useState('');
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const {fields} = useGatewayFields(GatewayType.S3);
  const visibleFields = BenchlingConfig.INSTRUMENTS_ENABLE_S3_IAM_ROLE_ACCESS_METHOD
    ? fields
    : (fields?.filter((f) => f.id !== S3_FIELD_IDS.CUSTOMER_ROLE_ARN) ?? null);

  const bucketName = fieldValues[S3_FIELD_IDS.CUSTOMER_BUCKET] || '';
  const allRequiredFieldsFilled =
    gatewayName && (visibleFields ?? []).filter((f) => f.isRequired).every((f) => fieldValues[f.id]?.trim());

  return (
    <Modal
      buttons={[
        {
          style: ButtonStyle.PRIMARY,
          text: 'Generate policy',
          disabled: !bucketName,
          onClick: () => {
            openBucketPolicyTab(bucketName.trim(), roleArn);
          },
          submit: false,
        },
        {
          style: ButtonStyle.PRIMARY,
          text: 'Configure gateway',
          disabled: !allRequiredFieldsFilled || fields == null,
          submit: true,
        },
      ]}
      title="Configure cloud gateway"
      onRequestClose={() => {
        onRequestClose();
      }}
      closeable={true}
      bannerProps={error != null ? {bannerType: InlineBannerType.ERROR, text: error.message} : null}
      onRequestSubmit={async () => {
        const submitFields: Record<string, string> = {};
        for (const [id, value] of Object.entries(fieldValues)) {
          submitFields[id] = value.trim();
        }
        setError(null);
        try {
          await withLoadingScreen(
            createGateway(gatewayName, 'S3', submitFields),
            'Setting up instrument gateway...'
          );
          onSave();
          onRequestClose();
          // eslint-disable-next-line benchlint/always-throw-from-catch
        } catch (err: unknown) {
          setError({fieldId: null, message: getUserMessage(err) ?? GENERIC_VALIDATION_FAILURE_MESSAGE});
        }
      }}
    >
      <div>
        <div>
          To connect to your S3 bucket, the following steps are required:
          <br />
          <br />
          1. Enter a Gateway Name for identification in Benchling
          <br />
          2. Provide your S3 Bucket Name
          <br />
          3. Generate and Apply the Policy to your Bucket
        </div>
        <br />
        <div className={css(styles.spacer)}>
          <TextField
            label="Gateway name"
            isRequired={true}
            value={gatewayName}
            onChange={(e) => {
              setGatewayName(e.target.value);
            }}
          />
          <br />
          {fields == null ? (
            <CenteredHorizontalThrobber />
          ) : (
            <GatewayFieldsForm
              fields={fields}
              errorFieldId={error?.fieldId}
              onValuesChange={(values) => {
                setFieldValues(values);
                setError(null);
              }}
            />
          )}
        </div>
      </div>
    </Modal>
  );
}

export const styles = StyleSheet.create({
  spacer: {
    marginTop: '10px',
  },
});
